import type { ZikrSetId } from '@sayfazkari/content';
import { readRaw, writeRaw } from './storage';
import { cloudWrite } from '../telegram/cloudStorage';
import { dayKey } from './time';

/**
 * История чтения по годам.
 *
 * Хранится строкой из 366 символов — по одному на день года, где
 * символ это маска: 1 = утренние прочитаны, 2 = вечерние, 3 = оба.
 * Наивный вариант `Record<дата, число>` вырос бы примерно до 6 КБ
 * за год и молча упёрся бы в лимит CloudStorage в 4096 символов.
 * Здесь год занимает 366 символов, то есть меньше десятой доли лимита.
 *
 * Год в ключе — чтобы старые годы не тащились в каждую запись.
 */

const MORNING_BIT = 1;
const EVENING_BIT = 2;
const DAYS = 366;

export function historyKey(year: number): string {
  return `sa_hist_${year}`;
}

function parseDayKey(key: string): { year: number; dayOfYear: number } {
  const [y, m, d] = key.split('-').map(Number);
  const year = y!;
  const date = new Date(year, m! - 1, d!);
  const start = new Date(year, 0, 1);
  const dayOfYear = Math.floor((date.getTime() - start.getTime()) / 86_400_000);
  return { year, dayOfYear };
}

function readYear(year: number): string {
  const raw = readRaw(historyKey(year));
  if (raw && raw.length === DAYS) return raw;
  return '0'.repeat(DAYS);
}

function writeYear(year: number, data: string): void {
  const key = historyKey(year);
  writeRaw(key, data);
  cloudWrite(key, data);
}

/** Принимает год из облака, если там дней больше, чем локально */
export function mergeYearFromCloud(year: number, remote: string): void {
  if (remote.length !== DAYS) return;
  const local = readYear(year);
  let merged = '';
  let changed = false;
  for (let i = 0; i < DAYS; i++) {
    // Маски объединяются побитово: набор, отмеченный на любом
    // устройстве, считается прочитанным
    const a = Number(local[i]) || 0;
    const b = Number(remote[i]) || 0;
    const both = a | b;
    if (both !== a) changed = true;
    merged += String(both);
  }
  if (changed) writeYear(year, merged);
}

export function recordCompletion(setId: ZikrSetId, at: Date = new Date()): void {
  const key = dayKey(at);
  const { year, dayOfYear } = parseDayKey(key);
  if (dayOfYear < 0 || dayOfYear >= DAYS) return;

  const data = readYear(year);
  const bit = setId === 'morning' ? MORNING_BIT : EVENING_BIT;
  const current = Number(data[dayOfYear]) || 0;
  const next = current | bit;
  if (next === current) return;

  writeYear(year, data.slice(0, dayOfYear) + String(next) + data.slice(dayOfYear + 1));
}

function maskFor(key: string): number {
  const { year, dayOfYear } = parseDayKey(key);
  if (dayOfYear < 0 || dayOfYear >= DAYS) return 0;
  return Number(readYear(year)[dayOfYear]) || 0;
}

/** Прочитан ли набор в этот день */
export function isDone(setId: ZikrSetId, at: Date = new Date()): boolean {
  const bit = setId === 'morning' ? MORNING_BIT : EVENING_BIT;
  return (maskFor(dayKey(at)) & bit) !== 0;
}

function shiftDays(key: string, delta: number): string {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(y!, m! - 1, d! + delta);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${mm}-${dd}`;
}

/**
 * Сколько дней подряд человек читал азкары, считая назад от сегодня.
 *
 * Сегодняшний день, если он ещё пустой, стрик НЕ обнуляет: день не
 * закончился. Иначе счётчик показывал бы ноль каждое утро до первого
 * прочтения — и обесценивал бы саму идею.
 */
export function currentStreak(at: Date = new Date()): number {
  const today = dayKey(at);
  let cursor = maskFor(today) !== 0 ? today : shiftDays(today, -1);
  let streak = 0;

  // Ограничение на случай испорченных данных: без него можно уйти в вечный цикл
  for (let i = 0; i < 2000; i++) {
    if (maskFor(cursor) === 0) break;
    streak++;
    cursor = shiftDays(cursor, -1);
  }
  return streak;
}

/** Сколько всего наборов прочитано за всё время */
export function totalSetsRead(): number {
  const thisYear = new Date().getFullYear();
  let total = 0;
  for (let year = thisYear - 5; year <= thisYear; year++) {
    for (const ch of readYear(year)) {
      const m = Number(ch) || 0;
      if (m & MORNING_BIT) total++;
      if (m & EVENING_BIT) total++;
    }
  }
  return total;
}
